import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { BookOpen, FileText, ListChecks, Sparkles, StickyNote } from 'lucide-react';
import { DOCUMENTS_QUERY, ME_QUERY } from '../graphql/queries';
import { AlmaOrb } from '../components/AlmaOrb';
import { DocRowMeta } from '../components/DocRowMeta';
import { FetchErrorCard } from '../components/FetchErrorCard';
import { InsightsSummary } from '../components/InsightsSummary';
import { displayTitle, typeFromPath } from '../lib/docView';

interface MeData {
  me: {
    id: string;
    email: string;
    displayName?: string;
  } | null;
}

interface DocumentSummary {
  path: string;
  title: string;
  type: string | null;
  updatedAt: string;
  treeLabel: string;
}

/** Row icon per display type (front-matter `type` else folder convention). */
function docTypeIcon(doc: DocumentSummary) {
  switch (doc.type ?? typeFromPath(doc.path)) {
    case 'journal':
      return BookOpen;
    case 'list':
      return ListChecks;
    case 'note':
      return StickyNote;
    default:
      return FileText;
  }
}

interface DocumentsData {
  documents: DocumentSummary[];
}

/** Time-of-day greeting in the local tz. */
function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/** "today", "yesterday" or "Jul 7, 2026" in the local tz. */
function formatUpdated(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return 'today';
  if (d.toDateString() === y.toDateString()) return 'yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Home page: a compact greeting, recently-updated documents with human names
 * and previews, and the insights highlight. The hero stays small (design
 * review P2.3) so real content — and the capture affordance — are on screen
 * without scrolling.
 */
export function HomePage() {
  const { data: meData } = useQuery<MeData>(ME_QUERY);
  const {
    data: docsData,
    loading: docsLoading,
    error: docsError,
  } = useQuery<DocumentsData>(DOCUMENTS_QUERY);

  const displayName = meData?.me?.displayName || meData?.me?.email || '';
  const firstName = displayName.split(/[\s@]/)[0];

  const recent = useMemo(() => {
    const docs = [...(docsData?.documents ?? [])];
    docs.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    return docs.slice(0, 6);
  }, [docsData?.documents]);

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-24">
      <section className="flex items-center gap-4 pt-2 sm:pt-4 animate-fade-in-up">
        <AlmaOrb size="md" animated />
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-display font-semibold text-[var(--text-primary)]">
            {getGreeting()}
            {firstName ? `, ${firstName}` : ''}.
          </h1>
          <p className="mt-0.5 text-sm sm:text-base text-[var(--text-secondary)]">
            Capture what's happening, in your own words.
          </p>
        </div>
      </section>

      <section
        aria-label="Recent documents"
        className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft animate-fade-in-up"
      >
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-lg font-display font-semibold text-[var(--text-primary)]">Recents</h2>
          <Link
            to="/docs"
            className="text-sm font-medium text-[var(--primary)] hover:text-[var(--primary-hover)]"
          >
            All documents
          </Link>
        </div>

        {docsError && !docsData ? (
          <FetchErrorCard>
            Something went wrong loading your documents. Try again in a moment.
          </FetchErrorCard>
        ) : docsLoading && !docsData ? (
          <div className="space-y-2.5" aria-hidden="true">
            <span className="skeleton block h-10 w-full rounded-xl" />
            <span className="skeleton block h-10 w-full rounded-xl" />
            <span className="skeleton block h-10 w-full rounded-xl" />
          </div>
        ) : recent.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-[var(--text-secondary)]">
              Nothing here yet. Tell me about your day and I'll start filing things for you.
            </p>
            <Link to="/alma" className="btn btn-primary mt-4 gap-2">
              <Sparkles className="w-4 h-4" aria-hidden /> Tell me
            </Link>
          </div>
        ) : (
          <ul className="space-y-1">
            {recent.map((doc) => {
              const updated = formatUpdated(doc.updatedAt);
              const Icon = docTypeIcon(doc);
              const type = doc.type ?? typeFromPath(doc.path);
              return (
                <li key={doc.path}>
                  <Link
                    to={`/docs/${doc.path}`}
                    className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-[color-mix(in_oklab,var(--text-primary)_5%,transparent)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
                  >
                    <Icon className="w-4 h-4 text-[var(--text-tertiary)] shrink-0" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-[var(--text-primary)]">
                        {displayTitle(doc.path, doc.title)}
                      </span>
                      {(type === 'list' || type === 'note') && (
                        <DocRowMeta path={doc.path} type={type} bar={false} />
                      )}
                    </span>
                    <span className="text-xs text-[var(--text-tertiary)] shrink-0">
                      {doc.treeLabel}
                      {updated ? ` · ${updated}` : ''}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <InsightsSummary />
    </div>
  );
}
